/**
 * Results Component
 *
 * Main container for displaying all assessment results
 * Emotion-driven flow: Emotion → Reality → Solutions → Vision → Urgency → Logic
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RecommendationCard from './RecommendationCard';

// New emotion-driven components
import ComfortImpactCard from './results/ComfortImpactCard';
import CurrentSystemRealityCard from './results/CurrentSystemRealityCard';
import SystemOptionsDisplay from './results/SystemOptionsDisplay';
import LifeWithNewSystemCard from './results/LifeWithNewSystemCard';
import UrgencyAndTimingCard from './results/UrgencyAndTimingCard';
import TransparentComparison from './results/TransparentComparison';

// Calculator utilities
import { projectCurrentSystemCosts, calculateComfortIssueCosts } from '../../utils/calculatorFunctions';

export default function Results({ results, state, onReset, onSave, isSaving, assessmentId, aiSummary, isGeneratingAI, aiError, onGenerateAI, isSharedView = false }) {
  const { contractor } = useAuth();

  // Climate data - use contractor's settings or defaults
  const CLIMATE_DATA = {
    coolingHours: contractor?.cooling_hours || 1200,
    heatingHours: contractor?.heating_hours || 4200,
    avgSummerTemp: 78,
    avgWinterTemp: 42
  };

  // Prepare data for new components
  const [currentSystemProjection, setCurrentSystemProjection] = useState(null);
  const [comfortCosts, setComfortCosts] = useState(null);

  useEffect(() => {
    // Calculate current system 5-year projection
    if (state.systemAge && state.annualEnergyCost && state.squareFootage) {
      const projection = projectCurrentSystemCosts({
        currentAge: state.systemAge,
        annualEnergyCost: state.annualEnergyCost,
        squareFootage: state.squareFootage,
        systemType: state.systemType,
        repairCost: state.repairCost || 0,
        yearsToProject: 5
      });
      setCurrentSystemProjection(projection);
    }

    // Calculate comfort issue costs
    if (state.issues) {
      const costs = calculateComfortIssueCosts({
        issues: state.issues,
        systemAge: state.systemAge,
        annualEnergyCost: state.annualEnergyCost
      });
      setComfortCosts(costs);
    }
  }, [state]);

  // Check if any comfort issues were reported
  const hasComfortIssues = state.issues && Object.values(state.issues).some(val => val === true);

  // Get utility rates from state
  const electricRate = state.electricProviders?.find(p => p.id === state.electricProviderId)?.rate || 0.12;
  const gasRate = state.gasProviders?.find(p => p.id === state.gasProviderId)?.rate || 1.20;

  // Build envelope object for calculations
  const envelope = {
    airTightness: state.envelope?.airTightness || 'average',
    insulationQuality: state.envelope?.insulationQuality || 'average',
    windowType: state.envelope?.windowType || 'double'
  };

  // Build assessment data object for rebate matching
  const assessmentData = {
    systemType: state.systemType,
    systemAge: state.systemAge,
    replacementContext: state.replacementContext,
    convertToHeatPump: state.convertToHeatPump,
    current_afue: state.current_afue,
    can_install_condensing: state.can_install_condensing
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '30px'
      }}>
        <h2 style={{
          margin: '0 0 10px 0',
          fontSize: '32px',
          fontWeight: '700',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Assessment Complete
        </h2>
        <p style={{
          margin: '0',
          color: '#64748b',
          fontSize: '16px'
        }}>
          Here's your comprehensive HVAC system analysis
        </p>
        {assessmentId && (
          <p style={{
            margin: '10px 0 0 0',
            padding: '8px 16px',
            background: '#dcfce7',
            color: '#14532d',
            fontSize: '14px',
            fontWeight: '600',
            borderRadius: '6px',
            display: 'inline-block'
          }}>
            ✅ Saved - Assessment ID: {assessmentId}
          </p>
        )}
      </div>

      {/* Primary Recommendation */}
      <RecommendationCard results={results} />

      {/* Utility Providers Info (Phase 15) */}
      {(state.electricProviders?.length > 0 || state.gasProviders?.length > 0) && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '25px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          marginBottom: '25px'
        }}>
          <h3 style={{
            margin: '0 0 15px 0',
            color: '#2c3e50',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            Utility Rates Used
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '15px'
          }}>
            {state.electricProviderId && (
              <div style={{
                padding: '15px',
                background: '#eff6ff',
                borderRadius: '8px',
                border: '1px solid #bfdbfe'
              }}>
                <div style={{
                  fontSize: '20px',
                  marginBottom: '5px'
                }}>
                  ⚡
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#64748b',
                  marginBottom: '4px',
                  fontWeight: '600'
                }}>
                  Electricity
                </div>
                <div style={{
                  fontSize: '15px',
                  color: '#1e40af',
                  fontWeight: '600',
                  marginBottom: '2px'
                }}>
                  {state.electricProviders.find(p => p.id === state.electricProviderId)?.provider_name || 'Selected Provider'}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#475569'
                }}>
                  ${(state.electricProviders.find(p => p.id === state.electricProviderId)?.rate || 0).toFixed(4)}/kWh
                </div>
              </div>
            )}
            {state.gasProviderId && (
              <div style={{
                padding: '15px',
                background: '#fef3c7',
                borderRadius: '8px',
                border: '1px solid #fcd34d'
              }}>
                <div style={{
                  fontSize: '20px',
                  marginBottom: '5px'
                }}>
                  🔥
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#64748b',
                  marginBottom: '4px',
                  fontWeight: '600'
                }}>
                  Natural Gas
                </div>
                <div style={{
                  fontSize: '15px',
                  color: '#92400e',
                  fontWeight: '600',
                  marginBottom: '2px'
                }}>
                  {state.gasProviders.find(p => p.id === state.gasProviderId)?.provider_name || 'Selected Provider'}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#475569'
                }}>
                  ${(state.gasProviders.find(p => p.id === state.gasProviderId)?.rate || 0).toFixed(4)}/therm
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* EMOTION-DRIVEN FLOW STARTS HERE */}

      {/* 1. EMOTION: Lead with customer's pain (if comfort issues reported) */}
      {hasComfortIssues && comfortCosts && (
        <ComfortImpactCard
          issues={state.issues}
          comfortCosts={comfortCosts}
          currentAge={state.systemAge}
          annualEnergyCost={state.annualEnergyCost}
        />
      )}

      {/* 2. REALITY: Show the truth about their current system */}
      {currentSystemProjection && (
        <CurrentSystemRealityCard
          currentAge={state.systemAge}
          currentSystemProjection={currentSystemProjection}
        />
      )}

      {/* 3. SOLUTIONS: Present system options with savings and rebates */}
      <SystemOptionsDisplay
        systemType={state.systemType}
        canInstallCondensing={state.can_install_condensing}
        currentAnnualEnergyCost={state.annualEnergyCost}
        currentAge={state.systemAge}
        squareFootage={state.squareFootage}
        climateData={CLIMATE_DATA}
        envelope={envelope}
        electricRate={electricRate}
        gasRate={gasRate}
        assessmentData={assessmentData}
      />

      {/* 4. VISION: Paint emotional picture of life with new system (if comfort issues) */}
      {hasComfortIssues && (
        <LifeWithNewSystemCard issues={state.issues} />
      )}

      {/* 5. URGENCY: Create natural urgency through facts */}
      <UrgencyAndTimingCard
        rebateGroups={[]} // Will be populated by SystemOptionsDisplay's rebate matching
        currentSystemPrice={8000} // TODO: Get from best system option
        currentAge={state.systemAge}
        annualEnergyCost={state.annualEnergyCost}
      />

      {/* 6. LOGIC: Transparent side-by-side comparison */}
      {currentSystemProjection && (
        <TransparentComparison
          repairCost={state.repairCost || 0}
          currentAge={state.systemAge}
          currentSystemProjection={currentSystemProjection}
          bestOption={{ starting_price: 8000 }} // TODO: Get from best system option
          bestOptionSavings={null} // TODO: Get from best system option
          totalRebates={0} // TODO: Get from rebate matching
        />
      )}

      {/* Action Buttons */}
      {!isSharedView && (
        <div style={{
          display: 'flex',
          gap: '15px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {/* Start New Assessment Button */}
          <button
            onClick={onReset}
            style={{
              padding: '14px 28px',
              fontSize: '16px',
              fontWeight: '600',
              border: '2px solid #667eea',
              borderRadius: '8px',
              background: 'white',
              color: '#667eea',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#667eea';
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'white';
              e.target.style.color = '#667eea';
            }}
          >
            🔄 Start New Assessment
          </button>

          {/* Print Report Button */}
          <button
            onClick={() => window.print()}
            style={{
              padding: '14px 28px',
              fontSize: '16px',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
            }}
          >
            🖨️ Print Report
          </button>
        </div>
      )}

      {/* Footer Note */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        background: '#f8fafc',
        borderRadius: '8px',
        fontSize: '13px',
        color: '#64748b',
        textAlign: 'center',
        lineHeight: '1.6'
      }}>
        <strong>Note:</strong> This assessment provides educational estimates based on typical conditions{' '}
        {contractor?.office_city && contractor?.office_state ? (
          <>in the {contractor.office_city}, {contractor.office_state} area (Climate Zone {contractor?.climate_zone || '4A'})</>
        ) : contractor?.climate_zone ? (
          <>in Climate Zone {contractor.climate_zone}</>
        ) : (
          <>in Climate Zone 4A</>
        )}.
        {' '}Actual costs, savings, and rebates will vary based on specific installation conditions, contractor pricing, and current program availability.
        Consult with a licensed HVAC professional for a detailed quote.
      </div>
    </div>
  );
}
